import React, { useRef, useCallback } from "react"
import {
  Box,
  Container,
  Flex,
  Grid,
  Heading,
  Text,
  Image,
} from "@theme-ui/components"

// Begin swiper
import SwiperCore, {
  Pagination,
  Mousewheel,
  A11y,
  Autoplay,
  EffectFade,
} from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import "swiper/swiper-bundle.min.css"
import RichContentStructuredText from "../richContentStructuredText"
import { GatsbyImage } from "gatsby-plugin-image"
import ArrowBlack from "../../images/Arrow_black.inline.svg"
import ChevronRight from "../../images/chevron-right.inline.svg"
import ChevronLeft from "../../images/chevron-left.inline.svg"
SwiperCore.use([Mousewheel, Pagination, A11y, Autoplay, EffectFade])

const ItemCarousel = ({ items }) => {
  const sliderRef = useRef(null)

  const handlePrevious = useCallback(() => {
    if (!sliderRef.current) return
    sliderRef.current.swiper.slidePrev()
  }, [])

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return
    sliderRef.current.swiper.slideNext()
  }, [])

  return (
    <Box
      sx={{
        mb: [0, 0, 7, 8],
        boxShadow: [
          "0 0 0 0 #ffffff",
          "0 0 0 0 #ffffff",
          "0 300px 0 0 #0098D7",
        ],
        backgroundColor: "secondary",
        ".swiper-slide": {
          height: "auto",
        },
      }}
    >
      <Swiper
        ref={sliderRef}
        spaceBetween={0}
        slidesPerView={1}
        autoHeight={false}
        draggable
        effect={"fade"}
        autoplay={{
          delay: 10500,
          disableOnInteraction: true,
        }}
        loop
      >
        {items.map((item, index) => (
          <SwiperSlide key={item.originalId}>
            <Item
              richContent={item}
              index={index}
              itemsCount={items.length}
              handlePrevious={handlePrevious}
              handleNext={handleNext}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  )
}

const Item = ({
  richContent,
  index,
  itemsCount,
  handlePrevious,
  handleNext,
}) => (
  <Grid columns={[1, 1, 2]} gap={0}>
    <Container
      sx={{
        minHeight: "329px",
        backgroundColor: "secondary",
        padding: [4, 4, 5, 6],
        py: [4, 5, 6],
        gridRow: [2, 2, 1],
      }}
    >
      <Flex
        sx={{
          flexDirection: "column",
          height: "100%",
          justifyContent: ["flex-start", "space-between"],
        }}
      >
        <Box sx={{ mb: [5, 5, 5, 0] }}>
          <Box
            sx={{
              display: ["none", "none", "flex"],
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                mr: [2],
                cursor: "pointer",
                svg: {
                  stroke: "dark",
                },
                "&:hover,&:focus": {
                  svg: {
                    stroke: "light",
                  },
                },
              }}
              onClick={handlePrevious}
            >
              <ArrowBlack />
            </Box>
            <Box sx={{ mb: [0] }}>
              <Text sx={{ color: "light" }}>0{index + 1}</Text> | 0{itemsCount}
            </Box>
            <Box
              sx={{
                ml: [2],
                cursor: "pointer",
                svg: {
                  transform: "rotate(180deg)",
                  stroke: "dark",
                },
                "&:hover,&:focus": {
                  svg: {
                    stroke: "light",
                  },
                },
              }}
              onClick={handleNext}
            >
              <ArrowBlack />
            </Box>
          </Box>
          <Heading
            sx={{
              fontSize: [5, 5, 7, 90],
              fontWeight: "300",
              color: "light",
              lineHeight: 1.1,
              mt: 2,
            }}
          >
            {richContent.title}
          </Heading>
        </Box>
        <Box
          sx={{
            p: {
              fontFamily: "roc",
              fontSize: [2, 3, 4],
            },
          }}
        >
          <RichContentStructuredText text={richContent.body} theme="dark" />
        </Box>
      </Flex>
    </Container>
    <Box
      sx={{
        height: ["360px", "600px", "auto"],
        ".gatsby-image-wrapper": {
          height: "100%",
          position: "relative",
          "&:after": {
            top:0,
            left:0,
            content: "''",
            position: "absolute",
            height: "100%",
            width: "100%",
            backgroundColor: "dark",
            opacity: 0.3,
          },
        },
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          right: [4],
          top: [4],
          zIndex: 2,
        }}
      >
        <Box sx={{ mb: [0], color: "light" }}>
          <Text sx={{ color: "light" }}>0{index + 1}</Text> | 0{itemsCount}
        </Box>
      </Box>
      <Flex
        sx={{
          display: ["flex", "flex", "none"],
          zIndex: 2,
          position: "absolute",
          bottom: [5],
          left: [3],
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            mr: [2],
            cursor: "pointer",
            backgroundColor: "light",
            borderRadius: "100%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            svg: {
              polyline: {
                stroke: "primary",
              },
            },
            "&:hover,&:focus": {
              backgroundColor: "primary",
              svg: {
                polyline: {
                  stroke: "light",
                },
              },
            },
          }}
          onClick={handlePrevious}
        >
          <ChevronLeft />
        </Box>
        <Box
          sx={{
            cursor: "pointer",
            backgroundColor: "light",
            borderRadius: "100%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            svg: {
              polyline: {
                stroke: "primary",
              },
            },
            "&:hover,&:focus": {
              backgroundColor: "primary",
              svg: {
                polyline: {
                  stroke: "light",
                },
              },
            },
          }}
          onClick={handleNext}
        >
          <ChevronRight />
        </Box>
      </Flex>
      <GatsbyImage image={richContent.image.gatsbyImageData} alt="" />
    </Box>
  </Grid>
)

export default ItemCarousel
